// src/lib/posthog/onboarding-events.ts
//
// Pure decision rules for the KYB wizard's step-level events. No db, no
// `window`, no react-hook-form import: the wizard component
// owns the `useEffect` that keys off the current step and calls these
// functions with the ref `{ key, index, enteredAt }` it already tracks; this
// module only turns those facts (and the wizard's error tree / submit
// response / uploaded doc-type sets) into capture-ready event descriptions,
// so it stays testable with literal tables and offsets the coverage ratchet
// before any IO lands.

/** The wizard's six tab keys (`onboarding-wizard.tsx`'s `STEPS`). */
export type WizardStepKey = "1" | "2" | "3" | "4" | "5" | "6";

/** The `step_key -> step_name` mapping, the single source of truth for it. */
export const STEP_NAMES: Record<WizardStepKey, string> = {
  "1": "company_info",
  "2": "payment_methods",
  "3": "company_registration",
  "4": "directors",
  "5": "ubos",
  "6": "documents_consent",
};

function isWizardStepKey(key: string): key is WizardStepKey {
  return Object.prototype.hasOwnProperty.call(STEP_NAMES, key);
}

/** Resolves a step's `step_key` / `step_name` pair, or `null` for an unknown key. */
export function stepInfo(key: string): { step_key: WizardStepKey; step_name: string } | null {
  if (!isWizardStepKey(key)) return null;
  return { step_key: key, step_name: STEP_NAMES[key] };
}

/**
 * The ref the wizard keeps across renders, keyed on the current step
 * (`{ key, index, enteredAt }`). `enteredAt` and `now` are epoch
 * milliseconds, always injected by the caller for determinism.
 */
export interface StepRef {
  key: string;
  index: number;
  enteredAt: number;
}

export type StepTransitionEvent =
  | { event: "onboarding_started"; step_key: WizardStepKey; step_name: string; resumed: boolean }
  | { event: "onboarding_step_viewed"; step_key: WizardStepKey; step_name: string }
  | { event: "onboarding_step_completed"; step_key: WizardStepKey; step_name: string; duration_ms: number }
  | { event: "onboarding_step_back"; step_key: WizardStepKey; step_name: string; duration_ms: number };

/**
 * `prev === null` is the wizard's first render, so it yields only
 * `onboarding_started` (`resumed` = the merchant did not start at step 1).
 * A later call where `next.key` differs from `prev.key` yields, for the
 * *previous* step, `onboarding_step_completed` (new index greater) or
 * `onboarding_step_back` (new index smaller) with `duration_ms`, followed by
 * `onboarding_step_viewed` for the new step. A call where `next.key` equals
 * `prev.key` (no tab change) yields nothing.
 */
export function stepTransition(
  prev: StepRef | null,
  next: { key: string; index: number },
  now: number,
): StepTransitionEvent[] {
  if (prev === null) {
    const info = stepInfo(next.key);
    if (!info) return [];
    return [{ event: "onboarding_started", step_key: info.step_key, step_name: info.step_name, resumed: next.index > 0 }];
  }

  if (next.key === prev.key) return [];

  const events: StepTransitionEvent[] = [];
  const prevInfo = stepInfo(prev.key);
  const nextInfo = stepInfo(next.key);
  const duration_ms = now - prev.enteredAt;

  if (prevInfo) {
    events.push(
      next.index > prev.index
        ? { event: "onboarding_step_completed", step_key: prevInfo.step_key, step_name: prevInfo.step_name, duration_ms }
        : { event: "onboarding_step_back", step_key: prevInfo.step_key, step_name: prevInfo.step_name, duration_ms },
    );
  }
  if (nextInfo) {
    events.push({ event: "onboarding_step_viewed", step_key: nextInfo.step_key, step_name: nextInfo.step_name });
  }

  return events;
}

/**
 * A node is an RHF `FieldError` leaf when it carries a string `type`
 * (`{ type, message?, ref?, types? }`); anything else is a container to keep
 * walking (a plain object per nested field, or a sparse array per field-array
 * index).
 */
function isFieldErrorLeaf(node: unknown): boolean {
  return (
    typeof node === "object" &&
    node !== null &&
    !Array.isArray(node) &&
    typeof (node as Record<string, unknown>).type === "string"
  );
}

function walkFieldErrors(node: unknown, path: string, paths: string[]): void {
  if (node === undefined || node === null) return;
  if (isFieldErrorLeaf(node)) {
    paths.push(path);
    return;
  }
  if (Array.isArray(node)) {
    node.forEach((item, index) => {
      if (item !== undefined) walkFieldErrors(item, `${path}.${index}`, paths);
    });
    return;
  }
  if (typeof node === "object") {
    for (const [key, value] of Object.entries(node as Record<string, unknown>)) {
      walkFieldErrors(value, path ? `${path}.${key}` : key, paths);
    }
  }
}

/**
 * Walks react-hook-form's `errors` tree (the `onInvalid` handler's
 * argument) and returns field *paths* only (e.g. `directors.0.dateOfBirth`),
 * built entirely from key names and array indices — the `message` and
 * `type` values living on each leaf are never read into the result, so a
 * value a merchant typed can never leak through here.
 */
export function invalidFieldPaths(errors: Record<string, unknown>): string[] {
  const paths: string[] = [];
  for (const [key, value] of Object.entries(errors)) {
    walkFieldErrors(value, key, paths);
  }
  return paths;
}

export type SubmitFailureReasonCode =
  | "no_draft"
  | "already_submitted"
  | "missing_documents"
  | "validation_failed"
  | "network"
  | "unknown";

/**
 * The outcome the wizard's submit handler observed against
 * `POST /api/onboarding/submit` (`src/app/api/onboarding/submit/route.ts`):
 * either a response with a status and parsed JSON body, or a fetch throw
 * (offline, aborted, DNS failure — no response to read a status from).
 */
export type SubmitOutcome = { kind: "response"; status: number; body: unknown } | { kind: "network" };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : [];
}

/**
 * Maps a submit outcome to one of the six `reason_code`s and, for
 * `missing_documents`, the `missing_document_types[]` property — 400 ->
 * `no_draft`, 409 -> `already_submitted`, 422 with a non-empty
 * `missingDocTypes` -> `missing_documents`, 422 with `fields` ->
 * `validation_failed`, a fetch throw -> `network`, anything else -> `unknown`.
 */
export function submitFailureReason(
  outcome: SubmitOutcome,
): { reason_code: SubmitFailureReasonCode; missing_document_types: string[] } {
  if (outcome.kind === "network") {
    return { reason_code: "network", missing_document_types: [] };
  }

  const { status, body } = outcome;
  const bodyRecord = isRecord(body) ? body : {};
  const missingDocTypes = stringArray(bodyRecord.missingDocTypes);

  if (status === 400) return { reason_code: "no_draft", missing_document_types: [] };
  if (status === 409) return { reason_code: "already_submitted", missing_document_types: [] };
  if (status === 422 && missingDocTypes.length > 0) {
    return { reason_code: "missing_documents", missing_document_types: missingDocTypes };
  }
  if (status === 422 && bodyRecord.fields !== undefined) {
    return { reason_code: "validation_failed", missing_document_types: [] };
  }
  return { reason_code: "unknown", missing_document_types: [] };
}

/**
 * `onboarding_document_uploaded`: the diff of doc
 * types after the wizard's `onDocumentTypesChange` callback fires.
 * `prev === null` is the initial load (a resumed application's already-
 * uploaded documents), which must emit nothing, so the caller passes `null`
 * only for that first callback and the previous `Set` on every one after.
 * Replacing a document of a type already present does not change the set,
 * so it emits nothing too (an accepted limit).
 */
export function newDocTypes(prev: ReadonlySet<string> | null, next: ReadonlySet<string>): string[] {
  if (prev === null) return [];
  const added: string[] = [];
  for (const type of next) {
    if (!prev.has(type)) added.push(type);
  }
  return added;
}
